import { motion, useScroll, useTransform, useInView } from 'framer-motion'
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle, XCircle } from 'lucide-react'
import { useState, useRef } from 'react'

import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api'
import contactBg from '../assets/Contact/background.avif'
import { useTranslation } from 'react-i18next'
import '../styles/contact.css'

const Contact = () => {
  const { t } = useTranslation()
  const containerRef = useRef(null)
  const formRef = useRef(null)
  const isInView = useInView(containerRef, { once: true, margin: "-100px" })

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  })

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    subject: '',
    message: ''
  })

  const [status, setStatus] = useState('idle')
  const [errors, setErrors] = useState({})

  // Google Maps configuration
  const mapContainerStyle = {
    width: '100%',
    height: '288px'
  }
  
  const center = {
    lat: 36.23349657285288,
    lng: 43.997810130681756
  }
  
  const GOOGLE_MAPS_API_KEY = 'AIzaSyD7qslp57Qaf79881XgA0q9fWTj6UNuc34' // Make sure this key allows localhost:5173

  const contactInfo = [
    {
      icon: Phone,
      label: t('contact.info.phone'),
      value: '+1 (760) 807-8805 Thaer Konda (CEO)\n\n+964 750 445 6518 Thaer Konda (CEO)\n\n+964 750 451 2596 Aras Abdoka (COO)\n\n+964 751 138 4036 Ailin Boya (HR & Contracting Officer)'
    },
    {
      icon: Mail,
      label: t('contact.info.email'),
      value: 'coo@al-konda.com\nco@al-konda.com'
    },
    {
      icon: MapPin,
      label: t('contact.info.address'),
      value: 'Ankawa Two Side Street, Erbil, Iraq'
    }
  ]

  const servicesList = [
    { value: 'Construction', label: t('header.servicesList.construction') },
    { value: 'Power Generation', label: t('header.servicesList.powerGeneration') },
    { value: 'Environmental', label: t('header.servicesList.environmental') },
    { value: 'Oil & Fuel Supply', label: t('header.servicesList.oilFuelSupply') },
    { value: 'Logistics', label: t('header.servicesList.logistics') },
    { value: 'General Trading', label: t('header.servicesList.generalTrading') },
    { value: 'Recycling', label: t('header.servicesList.recycling') },
    { value: 'Other', label: t('header.servicesList.other') }
  ]

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formData.name.trim()) newErrors.name = t('contact.form.errors.nameRequired')
    if (!formData.email.trim()) {
      newErrors.email = t('contact.form.errors.emailRequired')
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('contact.form.errors.emailInvalid')
    }
    if (!formData.phone.trim()) newErrors.phone = t('contact.form.errors.phoneRequired')
    if (!formData.subject.trim()) newErrors.subject = t('contact.form.errors.subjectRequired')
    if (!formData.message.trim()) newErrors.message = t('contact.form.errors.messageRequired')

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    setStatus('sending')
    const WEB3FORMS_KEY = '23ad3a28-9f94-40a8-8ec0-5cf1ee528d40'

    const formPayload = {
      access_key: WEB3FORMS_KEY,
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      service: formData.service || 'Not specified',
      subject: formData.subject,
      message: formData.message
    }

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formPayload)
      })

      const data = await response.json()

      if (data.success) {
        setStatus('success')
        setFormData({ name: '', email: '', phone: '', service: '', subject: '', message: '' })
        setErrors({})
        setTimeout(() => setStatus('idle'), 5000)
      } else {
        throw new Error('Form submission failed')
      }
    } catch (error) {
      console.error(error)
      setStatus('error')
      setTimeout(() => setStatus('idle'), 5000)
    }
  }

  return (
    <section ref={containerRef} id="contact" className="relative min-h-screen pt-32 sm:pt-36 md:pt-40 lg:pt-44 pb-16 sm:pb-20 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={contactBg}
          alt="Background"
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/60 to-black/70" />

      {/* Subtle floating elements */}
      <div className="absolute inset-0 overflow-hidden opacity-30 pointer-events-none">
        <motion.div
          style={{ y: useTransform(scrollYProgress, [0, 1], [0, -40]) }}
          className="absolute top-1/4 right-1/4 w-32 h-32 bg-white/5 backdrop-blur-3xl rounded-full border border-white/10"
        />
      </div>

      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white/5 backdrop-blur-2xl border border-white/10 shadow-2xl rounded-xl lg:rounded-2xl p-6 sm:p-8 lg:p-12">
          
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            className="mb-8 lg:mb-12"
          >
            <div className="text-white/50 text-xs font-medium tracking-[0.2em] uppercase mb-3">{t('contact.badge')}</div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight mb-4">{t('contact.title')}</h2>
            <p className="text-base lg:text-lg text-white/60 font-light max-w-2xl">{t('contact.subtitle')}</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            
            {/* Left Column - Info & LIVE MAP */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <h3 className="text-xl font-medium text-white mb-4">{t('contact.info.title')}</h3>
              
              <div className="space-y-4">
                {contactInfo.map((item, index) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                    transition={{ delay: 0.1 * index }}
                    className="group flex items-start gap-4 p-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all"
                  >
                    <div className="bg-white/10 p-3 rounded-xl group-hover:scale-110 transition-transform">
                      <item.icon className="text-white" size={16} />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-white text-sm mb-1">{item.label}</div>
                      <div className="text-white/60 text-sm whitespace-pre-line">{item.value}</div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* INTERACTIVE GOOGLE MAP */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
                className="w-full h-72 rounded-xl overflow-hidden border border-white/10 shadow-lg mt-6"
              >
                <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={center}
                    zoom={15}
                    options={{
                      styles: [
                        {
                          "featureType": "all",
                          "elementType": "geometry.fill",
                          "stylers": [{ "color": "#1a1a1a" }]
                        },
                        {
                          "featureType": "all",
                          "elementType": "labels.text.fill",
                          "stylers": [{ "color": "#ffffff" }]
                        },
                        {
                          "featureType": "water",
                          "elementType": "geometry",
                          "stylers": [{ "color": "#2c2c2c" }]
                        }
                      ],
                      disableDefaultUI: false,
                      zoomControl: true,
                      streetViewControl: false,
                      fullscreenControl: true
                    }}
                  >
                    <Marker
                      position={center}
                      title="Al-Konda - Ankawa, Erbil, Iraq"
                    />
                  </GoogleMap>
                </LoadScript>
              </motion.div>
            </motion.div>

            {/* Right Column - Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
            >
              <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-xl p-6 lg:p-8">
                <h3 className="text-xl font-medium mb-6 text-white">{t('contact.form.title')}</h3>
                <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <input
                        type="text"
                        name="name"
                        placeholder={t('contact.form.name')}
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full bg-white/10 border rounded-xl px-4 py-3 text-white placeholder-white/50 focus:ring-2 focus:ring-white/30 outline-none transition-all text-sm ${errors.name ? 'border-red-500/50' : 'border-white/20'}`}
                      />
                      {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder={t('contact.form.email')}
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full bg-white/10 border rounded-xl px-4 py-3 text-white placeholder-white/50 focus:ring-2 focus:ring-white/30 outline-none transition-all text-sm ${errors.email ? 'border-red-500/50' : 'border-white/20'}`}
                      />
                      {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="phone-input-wrapper">
                      <PhoneInput
                        country={'iq'}
                        value={formData.phone}
                        onChange={(phone) => setFormData({ ...formData, phone })}
                        containerClass="phone-input-container"
                        inputClass="phone-input-field"
                        enableSearch={true}
                      />
                      {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
                    </div>

                    <div className="relative">
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-white/30 outline-none transition-all text-sm appearance-none cursor-pointer"
                        style={{ color: formData.service ? 'white' : 'rgba(255,255,255,0.5)' }}
                      >
                        <option value="" disabled>{t('contact.form.service')}</option>
                        {servicesList.map((service) => (
                          <option key={service.value} value={service.value} className="bg-slate-900 text-white">{service.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <input
                    type="text"
                    name="subject"
                    placeholder={t('contact.form.subject')}
                    value={formData.subject}
                    onChange={handleInputChange}
                    className={`w-full bg-white/10 border rounded-xl px-4 py-3 text-white placeholder-white/50 focus:ring-2 focus:ring-white/30 outline-none text-sm ${errors.subject ? 'border-red-500/50' : 'border-white/20'}`}
                  />
                  {errors.subject && <p className="text-red-400 text-xs mt-1">{errors.subject}</p>}

                  <textarea
                    rows="4"
                    name="message"
                    placeholder={t('contact.form.message')}
                    value={formData.message}
                    onChange={handleInputChange}
                    className={`w-full bg-white/10 border rounded-xl px-4 py-3 text-white placeholder-white/50 focus:ring-2 focus:ring-white/30 outline-none resize-none text-sm ${errors.message ? 'border-red-500/50' : 'border-white/20'}`}
                  ></textarea>
                  {errors.message && <p className="text-red-400 text-xs mt-1">{errors.message}</p>}

                  <motion.button
                    type="submit"
                    disabled={status === 'sending' || status === 'success'}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    className={`w-full py-3.5 rounded-full font-medium flex items-center justify-center gap-2 transition-all text-sm ${
                      status === 'success' ? 'bg-green-500/20 border-green-500/50 text-green-400' :
                      status === 'error' ? 'bg-red-500/20 border-red-500/50 text-red-400' :
                      'bg-white/10 border border-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    {status === 'sending' ? <Loader2 size={18} className="animate-spin" /> : 
                     status === 'success' ? <CheckCircle size={18} /> : 
                     status === 'error' ? <XCircle size={18} /> : <Send size={18} />}
                    <span>{t(`contact.form.${status === 'idle' ? 'send' : status}`)}</span>
                  </motion.button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Contact